import * as React from 'react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useCompanyPageTitle } from '@user/features/CompanyIdentifier/hooks'
import {
  CardListViewPicker,
  PageHeader,
  SettingsPage,
  toast,
} from '@sales-center/components'
import {
  getOpportunities,
  getOpportunityExport,
} from '@user/store/opportunity/api'
import {
  selectOpportunityFilters,
  selectOpportunityPageState,
  setOpportunityPageMode,
} from '@user/store/opportunity/slice'

import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  selectUserPreferencesGeneralLoaded,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'

import { useSelector } from 'react-redux'
import FilterButton from '../Filters'
import {
  OpportunityFilters,
  OpportunityPageFilterChipList,
} from './OpportunityFilters'
import { OppPermissionProps } from './typings'
import {
  clearedFilters,
  defaultFilters,
  IOpportunity,
  OpportunityFilterSet,
  orderByOptions,
  setOpportunitiesPerPage,
  setOpportunityPage,
  useSaveOpportunityFilters,
} from '@user/store/opportunity'

import Box from '@mui/material/Box'
import { OpportunityCardView } from './OpportunityCardView'
import { OpportunityListView } from './OpportinutyListView'
import { selectOpportunityColumnOrder } from '@user/store/userPreferences'
import { defaultOpportunityColumns } from './OpportinutyListView/opportunityListColumns'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { validateOppFilters } from './OpportunityFilters/utils'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { HistoryRoutePathnames } from '@user/store/lead'
import { useTheme } from '@mui/material/styles'
import { useLocation } from 'react-router-dom'
import { useGetUserListOnMount } from '@user/store/users'
import { updateLoadingState } from '@sales-center/common'
import { EntityTypeTabs } from '@user/components/Leads/EntityTypeTabs'

export const OpportunityPage: FC<OppPermissionProps> = (props) => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const importName = params.get('importNameFilter')
  const importId = params.get('importIdFilter')

  const saveOpportunityFilters = useSaveOpportunityFilters()
  useGetUserListOnMount()

  const { shouldFetchList, multipleServiceLocations, ...pageState } =
    useSelector(selectOpportunityPageState)
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const defaultListViewUserGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.ListMode,
  )

  const mode =
    defaultListViewUserGeneralPreference?.value == 'list'
      ? 'list'
      : defaultListViewUserGeneralPreference?.value == 'map'
        ? 'map'
        : 'card'

  const defaultOpportunityFilterShow = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.OpportunityFilterShow,
  )
  const showFilters =
    defaultOpportunityFilterShow?.value == 'hide' ? false : true

  const columnOrder = useSelector(selectOpportunityColumnOrder)

  const { filterSet } = useSelector(selectOpportunityFilters)

  useEffect(() => {
    if (importName) {
      saveOpportunityFilters({
        ...defaultFilters,
        filters: {
          ...defaultFilters.filters,
          closed: {
            ...defaultFilters.filters.closed,
            value: 'empty',
          },
          importId: {
            ...defaultFilters.filters.importId,
            value: {
              mode: 'any',
              options: [{ value: importId, label: importName }],
            },
          },
        },
      })
      history.replaceState(null, '', `/#${location.pathname}`)
    }
  }, [importName])

  useEffect(() => {
    dispatch(setOpportunityPageMode(mode))
  }, [dispatch, mode])

  // Restore the user's saved "Rows per page" for the list view across refresh.
  // List mode otherwise resets perPage to the default; we re-apply the persisted
  // size when entering list mode (card view has no rows-per-page selector). Keyed
  // off the previous mode so it fires on entering list — not on every preference
  // change — which would fight the user's optimistic change during the async save.
  const prefsLoaded = useSelector(selectUserPreferencesGeneralLoaded)
  const persistedPageSizeValue = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.OpportunityPageSize,
  )?.value
  const prevModeRef = useRef<string | null>(null)

  useEffect(() => {
    const persistedPageSize = Number(persistedPageSizeValue)
    if (
      prefsLoaded &&
      mode === 'list' &&
      prevModeRef.current !== 'list' &&
      persistedPageSize
    ) {
      dispatch(setOpportunitiesPerPage(persistedPageSize))
    }
    prevModeRef.current = mode
  }, [prefsLoaded, mode, persistedPageSizeValue, dispatch])

  const setCardMode = useCallback(() => {
    sessionStorage.setItem('pageNumber', '1')
    sessionStorage.setItem('pageSize', '10')
    if (mode !== 'card') {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ListMode,
          value: 'card',
        }),
      )
    }
  }, [dispatch, mode])

  const setListMode = useCallback(() => {
    sessionStorage.setItem('pageNumber', '1')
    sessionStorage.setItem('pageSize', '50')
    if (mode !== 'list') {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ListMode,
          value: 'list',
        }),
      )
    }
  }, [dispatch, mode])

  useEffect(() => {
    if (shouldFetchList) {
      const cardCaptureRefresh = mode === 'list' ? false : true
      dispatch(getOpportunities(cardCaptureRefresh))
    }
  }, [dispatch, shouldFetchList, mode])

  const [filtersDisabled, setFiltersDisabled] = useState(false)

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2DKnockEnabled = useD2DKnockEnabled()
  const knockStatusEnabled = mapViewEnabled && d2DKnockEnabled

  const onClickExport = React.useCallback(
    async (multiSLSelected: boolean) => {
      if (pageState?.count > 50000) {
        toast.error(
          'Exports are limited to 50,000 records. Please contact support for any requests that exceed this limit.',
        )
      } else {
        const columns = columnOrder
          .filter((x) => x.hide === false)
          .map((x) => x.field)

        // Include knock status columns if the feature is on but the user's saved
        // column order hasn't been synced with them yet (first export before the
        // useAddMissingColumns hook has persisted the new fields).
        const missingKnockStatusFields = knockStatusEnabled
          ? ['knockStatus', 'knockStatusReason'].filter(
              (f) => !columnOrder.some((c) => c.field === f),
            )
          : []

        const baseColumns =
          columns && columns.length > 0 ? columns : defaultOpportunityColumns
        const finalColumns = [...baseColumns, ...missingKnockStatusFields]

        dispatch(updateLoadingState('visible'))
        const data = await dispatch(
          getOpportunityExport({
            columns: finalColumns,
            multiServiceLocations: multiSLSelected,
          }),
        )

        if (getOpportunityExport.fulfilled.match(data)) {
          dispatch(updateLoadingState('hidden'))
          return data.payload
        } else {
          dispatch(updateLoadingState('hidden'))
          toast.error('An Error occurred.  Unable to Export List.')
        }
      }
    },
    [pageState.count, columnOrder, dispatch, knockStatusEnabled],
  )

  const pageNumberPersistent = useCallback(() => {
    const previousPathname = sessionStorage.getItem('previousPathname')
    const currentPathname = sessionStorage.getItem('currentPathname')
    const persistedPageNumber = sessionStorage.getItem('pageNumber') ?? '1'
    const persistedPageSize = sessionStorage.getItem('pageSize') ?? '50'

    if (
      currentPathname?.includes(HistoryRoutePathnames.OPPORTUNITIES) &&
      previousPathname?.includes(HistoryRoutePathnames.OPPORTUNITY_DETAILS)
    ) {
      dispatch(setOpportunitiesPerPage(parseInt(persistedPageSize)))
      dispatch(setOpportunityPage(parseInt(persistedPageNumber)))
      return
    }
    sessionStorage.setItem('pageNumber', pageState.page?.toString())
    sessionStorage.setItem('pageSize', pageState.perPage?.toString())
  }, [dispatch, pageState.page, pageState.perPage])

  useEffect(() => setFiltersDisabled(false), [mode])

  useEffect(() => {
    if (pageState?.pages != 0) {
      pageNumberPersistent()
    }
  }, [pageNumberPersistent, pageState?.pages])

  const onShowFilters = useCallback(
    (_e, checked: boolean) => {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.OpportunityFilterShow,
          value: checked ? 'show' : 'hide',
        }),
      )
    },
    [dispatch],
  )
  const theme = useTheme()
  const pageTitle = useCompanyPageTitle()
  return (
    <SettingsPage
      {...pageTitle}
      padding={mode === 'list' ? '0 0 0 0' : '20px 40px 20px 40px'}
      headContent={
        <PageHeader
          dataTestId="oppCardViewHeader"
          alignItems="flex-end"
          noPadding
          flexDirection="row-reverse"
          flexWrap="wrap"
        >
          <Box marginLeft="auto" px={2} py={1}>
            <Stack
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              gap={1}
            >
              <FilterButton<IOpportunity, OpportunityFilterSet>
                initialValues={filterSet}
                defaultValues={defaultFilters}
                clearedValues={clearedFilters}
                orderOptions={orderByOptions}
                applyCallback={saveOpportunityFilters}
                filterComponent={OpportunityFilters}
                hideOrderBy={mode === 'list'}
                disabled={filtersDisabled}
                validate={validateOppFilters}
              />
              <CardListViewPicker
                mode={mode}
                cardClickCallback={setCardMode}
                listClickCallback={setListMode}
              />
            </Stack>
          </Box>
          <Stack flexDirection="row" alignItems="center" gap={1}>
            <EntityTypeTabs />
          </Stack>
        </PageHeader>
      }
    >
      <Stack flexDirection="column" alignItems="center" height="100%">
        <Box
          maxWidth={
            mode == 'card' ? theme.breakpoints?.values?.lg || '1200px' : 'auto'
          }
          width="100%"
          pr={mode == 'list' ? '20px' : '0'}
        >
          <Box display="flex" mb={0.7} mt={mode == 'card' ? 0 : 2}>
            <Box display="flex" flexGrow={1} ml={mode == 'card' ? 0 : 2}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'Source Sans Pro Semibold',
                }}
                data-test-id="cardViewHeader"
              >
                {pageState.count}{' '}
                {pageState.count == 1
                  ? 'Opportunity found'
                  : 'Opportunities found'}{' '}
              </Typography>
            </Box>
            <Box>
              {!filtersDisabled && filterSet.appliedCount > 0 && (
                <Box display="flex" alignItems="center">
                  <Switch
                    size="small"
                    checked={showFilters}
                    onChange={onShowFilters}
                    onClick={(evt) => evt.stopPropagation()}
                    data-test-id="showFilterSelectionSwitch"
                  />
                  <Typography variant="body2">Show Filter Selection</Typography>
                </Box>
              )}
            </Box>
          </Box>

          {showFilters &&
            filterSet.appliedCount > 0 &&
            (mode == 'card' || (mode == 'list' && !filtersDisabled)) && (
              <Box display="flex" mb={2.7}>
                <OpportunityPageFilterChipList />
              </Box>
            )}
        </Box>

        {mode === 'list' ? (
          <OpportunityListView
            {...props}
            {...pageState}
            exportClickCallback={onClickExport}
            setFiltersDisabled={setFiltersDisabled}
          />
        ) : mode === 'card' ? (
          <OpportunityCardView {...props} {...pageState} />
        ) : null}
      </Stack>
    </SettingsPage>
  )
}
OpportunityPage.displayName = 'OpportunityPage'
